import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  signal,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';
import { DOCUMENT, isPlatformBrowser, isPlatformServer, ViewportScroller } from '@angular/common';
import { filter, throttleTime } from "rxjs/operators";
import { BehaviorSubject, ReplaySubject, Subscription, takeUntil, timer } from 'rxjs';

import { TitleService } from './services/title.service';
import { CanonicalService } from './services/canonical.service';
import { WindowService } from 'core/services/window.service';
import { fromWindowEvent } from 'core/utils/rxjs/window-event';
import { AppService } from './app.service';
import { iconPromo } from 'main/icons/icon-promo';
import { isLoading } from 'core/utils/rxjs/is-loading.operator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { iconCheckRound } from 'main/icons/icon-check-round';
import { iconCopy } from 'main/icons/icon-copy';
import { iconClose } from 'main/icons/icon-close';
import { RoutesEnum } from './locale/routes.enum';
import { RouterBehavior } from 'core/utils/router';
import { CookieHubService } from './cookiehub.service';
import { ModalService } from './_core/modal/services/modal.service';
import { MediaQuery } from 'core/services/media-query.service';
import { ApiService } from './pages/sign-up/services/api.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  readonly promoIcon = iconPromo;
  readonly copyIcon = iconCopy;
  readonly checkIcon = iconCheckRound;
  readonly closeIcon = iconClose;

  @ViewChild('footerRef', {read: ElementRef})
  footerRef!: ElementRef<HTMLElement>;

  isExternalPage$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  hideToTopButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $showCopyCheck = signal<boolean>(false);
  $hideFooter = signal(false);
  $allowPromoLine = signal(false);
  $toTopBtnBackground = signal<'dark' | 'light'>('light');

  $hasPromoLine = computed(() => this.app.$promocode()?.line && !this.app.$promocode()?.cplClosed && this.$allowPromoLine());
  
  private scrollSub?: Subscription;
  private destroyRef = inject(DestroyRef);
  private readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private cookieHub: CookieHubService,
    protected app: AppService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private titleService: TitleService,
    private window: WindowService,
    private media: MediaQuery,
    private viewportScroller: ViewportScroller,
    private canonicalService: CanonicalService,
    private renderer: Renderer2,
    private modalService: ModalService,
    private api: ApiService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.api.setEntryPointFromUrl();
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isExternalPage$.next(this.activeRoute?.firstChild?.snapshot?.data?.['isExternalPage'] || false);
        this.hideToTopButton$.next(this.activeRoute?.firstChild?.snapshot?.data?.['hideToTopButton'] || false);
        this.titleService.updateMeta();
      });
      effect(() => {
        // TODO: set region -AT -DE 
        this.document.documentElement.lang = app.$langIso() || 'en';
        this.document.body.classList.remove(`lang-de`);
        this.document.body.classList.remove(`lang-en`);
        this.document.body.classList.add(`lang-${app.$langIso()}`);
        if(this.$toTopBtnBackground() === 'dark') {
          this.document.body.classList.add('on-dark');
        } else {
          this.document.body.classList.remove('on-dark');
        }
        if (this.$hasPromoLine()) {
          this.document.body.classList.add('has-promo-line');
        } else {
          this.document.body.classList.remove('has-promo-line');
        }
      });
      this.cookieHub.load(this.app.$langIso() || 'en');
      this.deferCalendlyLoad();
      if(isPlatformServer(this.platformId)) {
        this.renderer.addClass(this.document.body, 'ssr-env');
      } else {
        this.document.body.classList.remove('ssr-env');
      }
      this.renderer.addClass(this.document.body, 'app-navigated');
      RouterBehavior.getRouteDataChanges().subscribe(data => {
        this.$hideFooter.set(!!data.hideFooter);
        this.$allowPromoLine.set(!!data.allowPromoLine);
        if(isPlatformBrowser(this.platformId) && this.document) {
          if(data.appBackgroundLight) {
            this.app.setThemeColor('#eceef3');
            this.$toTopBtnBackground.set('light');
            this.observeScroll();
          } else {
            this.app.setThemeColor('#000');
            this.scrollSub?.unsubscribe();
            this.$toTopBtnBackground.set('dark');
          }
        }
        if(data.appBackgroundLight) {
          this.renderer.setStyle(this.document.documentElement, 'background', '#eceef3');
        } else {
          this.renderer.setStyle(this.document.documentElement, 'background', '#000');
        }
      });

      this.bindModalThemeChanges();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.titleService.boot();
    if(isPlatformServer(this.platformId)) {
      this.canonicalService.setCanonicalURL();
    }
    fromWindowEvent('resize').pipe(throttleTime(150, undefined, {trailing: true}), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.fixMobileViewport();
    });
    this.fixMobileViewport();

    this.router.events
      .pipe(filter((e): e is Scroll => e instanceof Scroll))
      .subscribe((e) => {
        const yOffset = this.media.is('medium down') ? 100 : this.$hasPromoLine() ? 198 : 128;
        this.viewportScroller.setOffset([0, yOffset]);
        if (e.position !== null) {
          // backward navigation
          this.viewportScroller.scrollToPosition([0, e.position[1]]);
          return;
        }
        if (e.anchor) {
          // anchor navigation
          this.viewportScroller.scrollToAnchor(e.anchor.replace('??', '?'));
          if(this.media.is('medium')) {
            this.window.scrollX(0);
          }
          return;
        }
        // forward navigation
        this.viewportScroller.scrollToPosition([0, 0]);
      });
  }

  fixMobileViewport() {
    if(isPlatformServer(this.platformId)) {
      return;
    }
    const viewport = this.document.querySelector('meta[name="viewport"]');
    if (!viewport) return;
    const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent);
    if (!isMobile || this.window.innerWidth > 1000) return;
    // If phone in portrait → real width is fine
    // If phone in landscape → force mobile layout width
    const isLandscape = window.innerWidth > window.innerHeight;
    if (isLandscape) {
      viewport.setAttribute(
        'content',
        'width=766'
      );
    } else {
      viewport.setAttribute(
        'content',
        'user-scalable=no, initial-scale=1, maximum-scale=1, minimum-scale=1, ' +
        'width=device-width, height=device-height, viewport-fit=cover'
      );
    }
  }

  observeScroll() {
    this.scrollSub?.unsubscribe();
    this.scrollSub = fromWindowEvent('scroll').pipe(throttleTime(200, undefined, {trailing: true}), takeUntil(this.destroy$)).subscribe(() => {
      const footerHeight = this.footerRef.nativeElement.clientHeight;
      const pageHeight = this.document.body.scrollHeight - this.window.innerHeight + 40;
      if((this.window.scrollY || 0) + footerHeight > pageHeight) {
        this.$toTopBtnBackground.set('dark');
      } else {
        this.$toTopBtnBackground.set('light');
      }
    });
  }

  copyToClipboard(text: string | undefined) {
    navigator.clipboard.writeText(text || '');
    timer(1200).pipe(isLoading(v => this.$showCopyCheck.set(v)), takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  closePromocodeLine() {
    this.app.closePromocodeLine();
  }

  openPricing() {
    this.router.navigate([RoutesEnum.PRICING]);
  }

  private bindModalThemeChanges(): void {
    let previousTheme: string | null = null;

    this.modalService.opened$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        previousTheme = this.app.themeColor;
        this.app.setThemeColor('#fff');
      });

    this.modalService.closed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (previousTheme !== null) {
          this.app.setThemeColor(previousTheme);
          previousTheme = null;
        }
      });
  }

  private deferCalendlyLoad() {
    if (!isPlatformBrowser(this.platformId)) return;
    const nativeWindow = this.window.native;
    const load = () => {
      if (this.document.querySelector('script[data-calendly]')) return;
      const script = this.document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.defer = true;
      script.setAttribute('data-calendly', 'true');
      this.document.head.appendChild(script);
    };
    if (nativeWindow && 'requestIdleCallback' in nativeWindow) {
      (nativeWindow as any).requestIdleCallback(load, { timeout: 2500 });
    } else if (nativeWindow) {
      (nativeWindow as any).setTimeout(load, 2500);
    } else {
      setTimeout(load, 2500);
    }
  }
}
